import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import BrandSettings from "./BrandSettings";
import SectionsManager from "./SectionsManager";
import MenuEditor from "./MenuEditor";
import AdminPages from "./AdminPages";

const SUBTABS = [
  { id: "brand", label: "Brand & Contact" },
  { id: "sections", label: "Homepage Sections" },
  { id: "nav", label: "Navigation" },
  { id: "quicklinks", label: "Quick Links" },
  { id: "pages", label: "Pages" },
];

export default function AdminWebsite() {
  const [sub, setSub] = useState("brand");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get("/admin/site");
      setData(data);
    } catch {
      setError("Could not load website settings. Please try again.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section data-testid="admin-website">
      <div className="mb-6 flex flex-wrap gap-2">
        {SUBTABS.map((s) => (
          <button
            key={s.id}
            data-testid={`website-subtab-${s.id}`}
            onClick={() => setSub(s.id)}
            className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
              sub === s.id
                ? "border-ink bg-ink text-cream"
                : "border-line bg-white text-[#5C5C5C] hover:border-rose hover:text-rose"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {error && (
        <p data-testid="website-error" className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600">
          {error}
        </p>
      )}

      {!data && !error && (
        <div className="flex justify-center py-20">
          <Loader2 size={26} className="animate-spin text-rose" />
        </div>
      )}

      {data && (
        <>
          {sub === "brand" && <BrandSettings settings={data.settings} onSaved={load} />}
          {sub === "sections" && <SectionsManager sections={data.sections} onChanged={load} />}
          {sub === "nav" && (
            <MenuEditor location="header" items={data.menu.filter((m) => m.location === "header")} onChanged={load} />
          )}
          {sub === "quicklinks" && (
            <MenuEditor location="footer" items={data.menu.filter((m) => m.location === "footer")} onChanged={load} />
          )}
          {sub === "pages" && <AdminPages pages={data.pages} onChanged={load} />}
        </>
      )}
    </section>
  );
}
